import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, MailCheck, ArrowLeft, Sparkles } from "lucide-react";
import { AUTH } from "../lib/testIds";
import { requestPasswordReset } from "../lib/api";

const formatDetail = (detail) => {
    if (!detail) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
            .filter(Boolean)
            .join(" · ");
    }
    if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
    return null;
};

export const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        const trimmed = email.trim();
        if (!trimmed) {
            setError("Enter the email on your account.");
            return;
        }
        try {
            setSubmitting(true);
            await requestPasswordReset(trimmed);
            setSent(true);
        } catch (err) {
            setError(
                formatDetail(err?.response?.data?.detail) ||
                    err?.message ||
                    "Something went wrong. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main
            data-testid={AUTH.forgotPage}
            className="relative z-10 mx-auto flex min-h-[70vh] max-w-lg items-center px-6 py-14 md:px-10 md:py-20"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-16 h-[320px] w-[320px] rounded-full bg-[#737a3d]/12 blur-3xl"
            />
            <div className="relative w-full rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 shadow-[0_20px_60px_rgba(46,50,32,0.10)] md:p-9">
                <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#737a3d] hover:underline"
                >
                    <ArrowLeft className="h-3 w-3" strokeWidth={2.5} />
                    Back to sign in
                </Link>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-white/60 px-3 py-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                    <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                        Password reset
                    </span>
                </div>
                <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-[#2e3220] sm:text-4xl">
                    Forgot your password?
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                    Enter the email you use to sign in and we&apos;ll send a reset
                    link. It expires in 30 minutes.
                </p>
                <div
                    data-testid="forgot-google-hint"
                    className="mt-4 rounded-2xl border border-[#737a3d]/25 bg-[#737a3d]/10 p-3 text-xs leading-relaxed text-[#5a6142]"
                >
                    <strong className="text-[#2e3220]">Not seeing the email?</strong>{" "}
                    Check spam first. Still nothing after a few minutes? Reach out to
                    the Lendly admin and they can generate a one-time reset link for
                    you.
                </div>

                {sent ? (
                    <div
                        data-testid={AUTH.forgotSuccess}
                        className="mt-7 rounded-2xl border border-[#737a3d]/30 bg-[#737a3d]/8 p-5"
                    >
                        <div className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#737a3d] text-[#F4F1EA]">
                                <MailCheck className="h-4 w-4" strokeWidth={2.5} />
                            </span>
                            <div>
                                <p className="font-display text-base font-bold text-[#2e3220]">
                                    Check your inbox
                                </p>
                                <p className="mt-1 text-sm leading-relaxed text-[#5a6142]">
                                    If an account exists for{" "}
                                    <span className="font-semibold text-[#2e3220]">
                                        {email.trim()}
                                    </span>
                                    , a reset link is on its way. Didn&apos;t get it? Check
                                    spam, or{" "}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSent(false);
                                        }}
                                        className="font-semibold text-[#737a3d] hover:underline"
                                    >
                                        try a different email
                                    </button>
                                    .
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        data-testid={AUTH.forgotForm}
                        className="mt-7 space-y-3"
                        noValidate
                    >
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Email
                            </span>
                            <div className="relative mt-1">
                                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a6142]" />
                                <input
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid={AUTH.forgotEmailInput}
                                    placeholder="you@neighbor.com"
                                    className="w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 py-3 pl-9 pr-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                                />
                            </div>
                        </label>

                        {error && (
                            <p
                                data-testid={AUTH.error}
                                role="alert"
                                className="rounded-2xl border border-[#B56C5A]/40 bg-[#B56C5A]/10 px-3 py-2 text-xs font-semibold text-[#7a3a30]"
                            >
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid={AUTH.forgotSubmit}
                            className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting && (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                            Send reset link
                        </button>
                    </form>
                )}

                <p className="mt-6 text-center text-xs text-[#5a6142]">
                    Remembered it?{" "}
                    <Link
                        to="/login"
                        className="font-semibold text-[#737a3d] hover:underline"
                    >
                        Sign in
                    </Link>
                </p>
            </div>
        </main>
    );
};

export default ForgotPassword;
